/**
 * 一覧の表の列の幅を変えて、この端末に覚える部品（#319、2026-11-05 射場さん ★7、docs/103 §9・docs/10 §5.11）。
 *   ・既定は中身に合わせる：auto の列＝その表の中身（見出しとセル）でいちばん広いものの幅（下限〜autoMax）。rest の列＝表の残り
 *   ・見出しの境のつまみ（ColumnGrip）をドラッグして幅を変える。つまみは Tab で止まり ← →（8px ずつ・Shift で 40px）でも動く。
 *     ダブルクリック（キーは Enter）で既定の幅（中身に合わせる・rest は残り）に戻す＝ドラッグだけにしない（WCAG 2.2 SC 2.5.7）
 *   ・覚える所はこの端末（localStorage gb.cols.<アカウント>.<表>＝{"to":420,"how":210,"by":200}）。読み書きに失敗しても既定の幅で動く。
 *     同じ表の鍵なら、どの画面に置いた表も同じ設定（送付の記録は 窓・{customer}の詳細・{project}のタブ「送付」・送付状の一覧）
 *   ・rest の列に幅を決めると、表の右の余りは「埋め」の列（ListColumnsFill）が取る＝列はどれも決めた幅のまま。表の幅が枠より広くなれば枠が横に流れる
 *   ・スマホ・狭い幅のカードには付けない（呼ぶ側が表を描かない）
 * 一覧の列の幅を変えられるようにするときはこの部品を使う（docs/10 §5.11）
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { useMe } from "../auth.js";

export type ColumnSpec = {
  /** 見出しの名前（つまみの読み上げ「{label}の幅」） */
  label: string;
  /** 手で決められる幅の下限・上限（px） */
  min: number;
  max: number;
  /** auto＝中身に合わせる（既定の上限 autoMax）／rest＝表の残り */
  fit: "auto" | "rest";
  /** 中身に合わせるときの上限（省けば max） */
  autoMax?: number;
};

const KEY = "gb.cols.";
const STEP = 8;
const STEP_BIG = 40;

const clamp = (n: number, s: ColumnSpec) => Math.round(Math.min(s.max, Math.max(s.min, n)));

function readStored<K extends string>(account: string | null, table: string, spec: Record<K, ColumnSpec>): Partial<Record<K, number>> {
  if (!account) return {};
  try {
    const raw = window.localStorage.getItem(`${KEY}${account}.${table}`);
    if (!raw) return {};
    const v = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<Record<K, number>> = {};
    for (const k of Object.keys(spec) as K[]) {
      const n = v[k];
      if (typeof n === "number" && Number.isFinite(n)) out[k] = clamp(n, spec[k]);
    }
    return out;
  } catch {
    return {};
  }
}
function writeStored(account: string | null, table: string, v: Record<string, number>): void {
  if (!account) return;
  try {
    const key = `${KEY}${account}.${table}`;
    if (Object.keys(v).length) window.localStorage.setItem(key, JSON.stringify(v));
    else window.localStorage.removeItem(key);
  } catch {
    /* 覚えられなくても画面は動く（既定の幅） */
  }
}

/**
 * セルの中身の幅（小数のまま・「…」で切れていても全部）＋左右の余白。つまみ（.lcgrip。見出しの右端に重ねた箱）は数えない
 * （数えると、いまの列の幅を測ることになり、測るたびに広がる）。中の要素は Range で中身の字・札の箱を測る（枠からはみ出した札も）
 */
function cellWidth(cell: HTMLElement): number {
  const cs = getComputedStyle(cell);
  let left = Infinity;
  let right = -Infinity;
  for (const n of cell.childNodes) {
    if (n instanceof HTMLElement && n.classList.contains("lcgrip")) continue;
    const r = document.createRange();
    if (n.nodeType === Node.TEXT_NODE) r.selectNode(n);
    else r.selectNodeContents(n);
    for (const b of r.getClientRects()) {
      if (b.width <= 0) continue;
      left = Math.min(left, b.left);
      right = Math.max(right, b.right);
    }
  }
  return (right > left ? right - left : 0) + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
}

export type ListColumns<K extends string> = {
  /** <col> に付ける幅（undefined＝rest の列で、残りを取る） */
  colStyle: (k: K) => CSSProperties;
  /** 「埋め」の列の幅（rest の列に幅を決めたときだけ残りを取る） */
  fillStyle: CSSProperties;
  /** 表に付ける最小の幅（列の合計。枠より広ければ枠が横に流れる） */
  tableStyle: CSSProperties;
  /** つまみの props（見出しのセルの中に ColumnGrip で置く） */
  grip: (k: K) => GripProps;
};
type GripProps = {
  label: string;
  now: number;
  min: number;
  max: number;
  onPointerDown: (e: PointerEvent<HTMLSpanElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLSpanElement>) => void;
  onDoubleClick: () => void;
  testId: string;
};

/**
 * 表の列の幅（docs/10 §5.11）。fixed＝決まった幅の列（px）、spec＝変えられる列。
 * 中身に合わせる幅は表（table）の中の data-col="<列>" のセル（見出しも）を測る。deps が変わったら（行が変わったら）測り直す
 */
export function useListColumns<K extends string>(
  table: string,
  spec: Record<K, ColumnSpec>,
  fixed: number,
  tableRef: RefObject<HTMLTableElement | null>,
  deps: unknown,
): ListColumns<K> {
  const me = useMe().data;
  const account = me?.session.accountId ?? null;
  const [stored, setStored] = useState<Partial<Record<K, number>>>(() => readStored(account, table, spec));
  // ログインし直して人が変わったら、その人の覚えた幅で読み直す
  const seen = useRef(account);
  if (seen.current !== account) {
    seen.current = account;
    setStored(readStored(account, table, spec));
  }
  const [fit, setFit] = useState<Partial<Record<K, number>>>({});
  const keys = useMemo(() => Object.keys(spec) as K[], [spec]);
  // 中身に合わせる幅を測る（行が入れ替わる・Web フォントを読み終えるたびに。描く前＝押す所はずれない）
  useLayoutEffect(() => {
    const t = tableRef.current;
    if (!t) return;
    const measure = () => {
      const next: Partial<Record<K, number>> = {};
      for (const k of keys) {
        const s = spec[k];
        if (s.fit !== "auto") continue;
        const cells = [...t.querySelectorAll<HTMLElement>(`[data-col="${k}"]`)];
        const w = Math.ceil(Math.max(0, ...cells.map(cellWidth)));
        next[k] = Math.round(Math.min(s.autoMax ?? s.max, Math.max(s.min, w)));
      }
      setFit((cur) => (keys.every((k) => cur[k] === next[k]) ? cur : next));
    };
    measure();
    let live = true;
    const fonts = document.fonts;
    const again = () => {
      if (live) measure();
    };
    void fonts?.ready.then(again);
    fonts?.addEventListener("loadingdone", again);
    return () => {
      live = false;
      fonts?.removeEventListener("loadingdone", again);
    };
  }, [tableRef, keys, spec, deps]);
  const widthOf = useCallback((k: K): number | undefined => stored[k] ?? (spec[k].fit === "auto" ? (fit[k] ?? spec[k].min) : undefined), [stored, fit, spec]);
  const save = useCallback(
    (next: Partial<Record<K, number>>) => {
      setStored(next);
      writeStored(account, table, next as Record<string, number>);
    },
    [account, table],
  );
  const setOne = useCallback(
    (k: K, w: number | null, persist: boolean) => {
      setStored((cur) => {
        const next = { ...cur };
        if (w == null) delete next[k];
        else next[k] = clamp(w, spec[k]);
        if (persist) writeStored(account, table, next as Record<string, number>);
        return next;
      });
    },
    [account, table, spec],
  );
  /** いま描いている幅（rest の列で幅を決めていないときは見出しのセルの幅） */
  const shownWidth = useCallback(
    (k: K): number => {
      const w = widthOf(k);
      if (w != null) return w;
      const th = tableRef.current?.querySelector<HTMLElement>(`th[data-col="${k}"]`);
      return Math.round(th?.getBoundingClientRect().width ?? spec[k].min);
    },
    [widthOf, tableRef, spec],
  );
  const restSet = keys.some((k) => spec[k].fit === "rest" && stored[k] != null);
  const sum = fixed + keys.reduce((a, k) => a + (widthOf(k) ?? spec[k].min), 0);
  return {
    colStyle: (k) => {
      const w = widthOf(k);
      return w == null ? {} : { width: `${w}px` };
    },
    fillStyle: restSet ? {} : { width: 0 },
    tableStyle: { minWidth: `${sum}px` },
    grip: (k) => ({
      label: spec[k].label,
      now: shownWidth(k),
      min: spec[k].min,
      max: spec[k].max,
      testId: `colgrip-${k}`,
      onPointerDown: (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const el = e.currentTarget;
        const x0 = e.clientX;
        const w0 = shownWidth(k);
        el.setPointerCapture(e.pointerId);
        el.classList.add("is-drag");
        const move = (ev: globalThis.PointerEvent) => setOne(k, w0 + (ev.clientX - x0), false);
        const up = (ev: globalThis.PointerEvent) => {
          el.removeEventListener("pointermove", move);
          el.removeEventListener("pointerup", up);
          el.removeEventListener("pointercancel", up);
          el.classList.remove("is-drag");
          // 動かしていない（ダブルクリックの1回目など）なら覚えない
          if (ev.clientX !== x0) setOne(k, w0 + (ev.clientX - x0), true);
        };
        el.addEventListener("pointermove", move);
        el.addEventListener("pointerup", up);
        el.addEventListener("pointercancel", up);
      },
      onKeyDown: (e) => {
        const step = e.shiftKey ? STEP_BIG : STEP;
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          setOne(k, shownWidth(k) + (e.key === "ArrowRight" ? step : -step), true);
        } else if (e.key === "Enter") {
          e.preventDefault();
          setOne(k, null, true);
        }
      },
      onDoubleClick: () => {
        const next = { ...stored };
        delete next[k];
        save(next);
      },
    }),
  };
}

/** 見出しの境のつまみ（見出しのセル＝position:relative の右端）。読み上げ「{名前}の幅」・← → で動く・ダブルクリック／Enter で既定の幅 */
export function ColumnGrip({ g }: { g: GripProps }) {
  return (
    <span
      className="lcgrip"
      role="separator"
      aria-orientation="vertical"
      aria-label={`${g.label}の幅`}
      aria-valuenow={g.now}
      aria-valuemin={g.min}
      aria-valuemax={g.max}
      title={`${g.label}の幅（ドラッグ・← →で変更、ダブルクリックで元の幅）`}
      tabIndex={0}
      onPointerDown={g.onPointerDown}
      onKeyDown={g.onKeyDown}
      onDoubleClick={g.onDoubleClick}
      onClick={(e) => e.stopPropagation()}
      data-testid={g.testId}
    />
  );
}
